import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { StepIndicator } from '../components/StepIndicator';
import { FocusHeader } from '../components/FocusHeader';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps, useWizardNavigation, nextIncompleteRoute } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_PATHS, backUrlFor, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { waitForSessionUpdate } from '../lib/navigationHelpers';
import { ensureHtmlContent } from '../lib/textToHtml';
import { logError } from '../lib/errorLogger';
import { fetchWithHeadStartRetry } from '../lib/fetchWithHeadStartRetry';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import {
  useEditorImages,
  blogImageExtensions,
  editorImageClickHandler,
  noEditorImageHandlers,
  type EditorImageHandlers,
} from '../hooks/useEditorImages';
import { EditorImageTools } from '../components/EditorImageTools';
import {
  Sparkles,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link2,
  ImagePlus,
  LayoutGrid,
  Heading2,
  Heading3,
  Highlighter,
  Quote,
  Undo,
  Redo,
  Save,
} from 'lucide-react';

export function ArticleEdit() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user } = useSession();
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showAdjustForm, setShowAdjustForm] = useState(false);
  const [adjustRequest, setAdjustRequest] = useState('');
  const [regenerating, setRegenerating] = useState(false);
  const [navigating, setNavigating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [sessionValidationError, setSessionValidationError] = useState<string | null>(null);
  const [imageError, setImageError] = useState('');
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout>();
  // De editor bestaat eerder dan de hook; via deze ref bereikt de editor-config de foto-functies.
  const imageHandlersRef = useRef<EditorImageHandlers>(noEditorImageHandlers);
  // Echte 'vuil'-vlag: gezet bij elke wijziging, gewist na een geslaagde save.
  const [dirty, setDirty] = useState(false);
  // Laatst bekende inhoud en sessie-id, zodat een wachtende autosave ook bij unmount weg kan.
  const latestHtmlRef = useRef<string | null>(null);
  const sessionIdRef = useRef<string | null>(null);

  const sessionId = sessionIdFromLocation(location);

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sessionId: string) => {
    try {
      setLoading(true);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sessionId)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('Error loading session:', sessionError);
        await logError({
          errorType: 'session_load_error',
          errorMessage: `Failed to load session ${sessionId}`,
          stackTrace: sessionError ? JSON.stringify(sessionError) : 'Session not found',
          sessionId,
        });
        setSessionValidationError('Sessie kon niet worden geladen.');
        setLoading(false);
        return;
      }

      if (!sessionData.article_content) {
        console.warn('Session loaded but has no article_content:', sessionData.state);
        setSessionValidationError(
          `Deze sessie heeft nog geen artikel content. Huidige status: ${sessionData.state}. ` +
          `Je moet eerst het artikel genereren voordat je het kunt bewerken.`
        );
        setLoading(false);
        return;
      }

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      if (userError || !userData) {
        console.error('Error loading user:', userError);
        await logError({
          errorType: 'user_load_error',
          errorMessage: `Failed to load user for session ${sessionId}`,
          stackTrace: userError ? JSON.stringify(userError) : 'User not found',
          sessionId,
        });
        setSessionValidationError('Gebruiker kon niet worden geladen.');
        setLoading(false);
        return;
      }

      setDashboardSession(sessionData);
      setDashboardUser(userData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      await logError({
        errorType: 'dashboard_session_load_exception',
        errorMessage: `Exception loading dashboard session: ${errorMessage}`,
        stackTrace: err instanceof Error ? err.stack : undefined,
        sessionId,
      });
      setSessionValidationError('Er ging iets mis bij het laden van de sessie.');
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;

  useEffect(() => {
    sessionIdRef.current = activeSession?.id ?? null;
  }, [activeSession?.id]);

  const activeUser = dashboardUser || user;
  const { handleStepClick } = useWizardNavigation(activeSession);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#FF6B35] underline hover:text-[#E55A2B]',
        },
      }),
      ...blogImageExtensions(imageHandlersRef),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-lg max-w-none focus:outline-none min-h-[400px] px-4 py-3',
      },
      handleClickOn: editorImageClickHandler(imageHandlersRef),
    },
    onUpdate: ({ editor: updatedEditor }) => {
      latestHtmlRef.current = updatedEditor.getHTML();
      setDirty(true);
      scheduleAutoSave();
    },
  });

  const images = useEditorImages({
    editor,
    userId: activeUser?.id,
    target: activeSession?.id ? { context: 'wizard-inline', sessionId: activeSession.id } : null,
    handlersRef: imageHandlersRef,
    onError: setImageError,
    // scheduleAutoSave staat verderop in dit bestand; via deze omweg wordt hij pas bij aanroep opgezocht.
    onChanged: () => {
      setDirty(true);
      scheduleAutoSave();
    },
  });

  // De noodopslag draait bij unmount (lege dependency-lijst) en mag de teller van lopende
  // uploads daarom niet rechtstreeks lezen; via deze ref blijft hij per render actueel.
  const imagesRef = useRef(images);
  imagesRef.current = images;

  useEffect(() => {
    if (loading) return;
    if (activeSession?.article_content && editor && !editor.isDestroyed) {
      const htmlContent = ensureHtmlContent(activeSession.article_content);
      latestHtmlRef.current = htmlContent;
      editor.commands.setContent(htmlContent, { emitUpdate: false });

      if (activeSession.article_last_edited) {
        setLastSaved(new Date(activeSession.article_last_edited));
      }
    }
  }, [activeSession, editor, loading]);

  const scheduleAutoSave = () => {
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    autoSaveTimeoutRef.current = setTimeout(() => {
      autoSaveTimeoutRef.current = undefined;
      void handleAutoSave();
    }, 2000);
  };

  const handleAutoSave = async (): Promise<boolean> => {
    // Ook niet opslaan vanuit een verlaten scherm: de editor is dan al afgebroken.
    if (!activeSession || !editor || editor.isDestroyed) return false;

    // Lopen er nog uploads, dan staan er blob-URL's in de tekst: straks opnieuw proberen.
    if (!images.prepareForSave()) {
      scheduleAutoSave();
      return false;
    }

    try {
      const content = editor.getHTML();

      const { error: saveError } = await supabase
        .from('sessions')
        .update({
          article_content: content,
          article_last_edited: new Date().toISOString(),
        })
        .eq('id', activeSession.id);

      if (saveError) {
        console.error('Error auto-saving:', saveError);
        await logError({
          errorType: 'article_autosave_error',
          errorMessage: `Failed to auto-save article: ${saveError.message}`,
          stackTrace: JSON.stringify(saveError),
          sessionId: activeSession.id,
        });
        return false;
      }

      setLastSaved(new Date());
      // Alleen 'schoon' als er intussen niets nieuws getypt is.
      if (latestHtmlRef.current === null || latestHtmlRef.current === content) {
        setDirty(false);
      }
      return true;
    } catch (error) {
      console.error('Error auto-saving:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      await logError({
        errorType: 'article_autosave_exception',
        errorMessage: `Exception during auto-save: ${errorMessage}`,
        stackTrace: error instanceof Error ? error.stack : undefined,
        sessionId: activeSession?.id,
      });
      return false;
    }
  };

  // Wachtende autosave direct wegschrijven, ook als het scherm al verdwijnt
  // (terugpijl, bolletje, browser-terug). Geen await: fire-and-forget op refs.
  const flushPendingSave = () => {
    if (!autoSaveTimeoutRef.current) return;
    // Eerst de timer stoppen, anders blijft die na het verlaten van het scherm opslaan.
    clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = undefined;
    // Lopen er nog uploads, dan niet wegschrijven: anders belanden blob-URL's in de tekst.
    if (imagesRef.current.pendingUploads() > 0) return;
    const html = latestHtmlRef.current;
    const id = sessionIdRef.current;
    if (html && id) {
      void supabase
        .from('sessions')
        .update({ article_content: html, article_last_edited: new Date().toISOString() })
        .eq('id', id);
    }
  };

  // Eerst opslaan, dan pas navigeren. Mislukt de save, dan blijven we hier.
  const saveThen = async (go: () => void) => {
    if (images.pendingUploads() > 0) {
      alert('Nog even geduld: een afbeelding wordt nog geüpload.');
      return;
    }
    if (!dirty && !autoSaveTimeoutRef.current) { go(); return; }
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
      autoSaveTimeoutRef.current = undefined;
    }
    const ok = await handleAutoSave();
    if (!ok) {
      alert('Opslaan is niet gelukt. Controleer je verbinding en probeer het opnieuw.');
      return;
    }
    go();
  };

  const handleManualSave = async () => {
    if (!activeSession || !editor || saving) return;

    if (images.pendingUploads() > 0) {
      alert('Nog even geduld: een afbeelding wordt nog geüpload.');
      return;
    }

    setSaving(true);
    try {
      await handleAutoSave();
    } finally {
      setSaving(false);
    }
  };

  // Terugpijl: één stap terug naar Richting (alleen-lezen zodra het artikel er is), na opslaan.
  const goBack = () => {
    const back = backUrlFor(WIZARD_PATHS.articleEdit, activeSession?.id);
    if (back) void saveThen(() => navigate(back));
  };

  // Sluiten: opslaan, dan naar Mijn Blogs.
  const handleClose = () => {
    void saveThen(() => navigate(WIZARD_EXIT_ROUTE));
  };

  const handleContinue = async () => {
    if (navigating || !activeSession?.id || !editor) return;

    if (images.pendingUploads() > 0) {
      alert('Nog even geduld: een afbeelding wordt nog geüpload.');
      return;
    }

    try {
      setNavigating(true);

      console.log('Saving article before continuing...');
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
        autoSaveTimeoutRef.current = undefined;
      }
      const saved = await handleAutoSave();
      if (!saved) {
        alert('Opslaan is niet gelukt. Controleer je verbinding en probeer het opnieuw.');
        setNavigating(false);
        return;
      }

      console.log('Verifying article content is saved...');
      const verified = await waitForSessionUpdate(activeSession.id, 'article_content', 10, 500);

      if (!verified) {
        await logError({
          errorType: 'article_save_verification_failed',
          errorMessage: 'Article content could not be verified in database after save',
          sessionId: activeSession.id,
        });
        alert('Er ging iets mis bij het opslaan. Probeer het opnieuw.');
        setNavigating(false);
        return;
      }

      // Alleen de eerste keer van 'artikel geschreven' naar 'artikel goedgekeurd'.
      // Een latere terugkeer naar de editor mag de status (en dus de hervat-stap) niet terugzetten.
      // Sessies komen als 'idea_approved' de editor binnen ('article_generated' wordt nergens gezet).
      // Alleen vanuit die vroege statussen doorzetten; een latere status nooit terugdraaien.
      if (activeSession.state === 'idea_approved' || activeSession.state === 'article_generated') {
        console.log('Updating session state to article_approved...');
        const { error: updateError } = await supabase
          .from('sessions')
          .update({ state: 'article_approved' })
          .eq('id', activeSession.id);

        if (updateError) {
          console.error('Error updating session state:', updateError);
          await logError({
            errorType: 'session_state_update_error',
            errorMessage: `Failed to update session state to article_approved: ${updateError.message}`,
            stackTrace: JSON.stringify(updateError),
            sessionId: activeSession.id,
          });
          throw new Error('Kon sessie status niet bijwerken');
        }
      }

      const targetRoute = nextIncompleteRoute(activeSession);
      console.log('Navigating to:', targetRoute);
      navigate(wizardUrl(targetRoute, activeSession.id));
    } catch (error) {
      console.error('Error continuing:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      await logError({
        errorType: 'article_continue_error',
        errorMessage: `Error continuing from article edit: ${errorMessage}`,
        stackTrace: error instanceof Error ? error.stack : undefined,
        sessionId: activeSession?.id,
      });
      alert('Er ging iets mis. Probeer het opnieuw.');
      setNavigating(false);
    }
  };

  const handleAdjust = async () => {
    if (!adjustRequest.trim()) {
      alert('Geef aan wat er anders moet.');
      return;
    }

    if (!editor) return;

    setRegenerating(true);

    try {
      const currentContent = editor.getHTML();
      const originalContent = currentContent;

      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

      console.log('[ArticleEdit] Sending regenerate request to Edge Function...');

      // Head-start fetch met stille retry: een losse netwerk-hapering wordt één
      // keer opnieuw geprobeerd voordat we de gebruiker een fout tonen.
      const { networkError } = await fetchWithHeadStartRetry(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            type: 'article_regenerate',
            session_id: activeSession?.id,
            user_id: activeUser?.id,
            data: {
              article_content: currentContent,
              change_request: adjustRequest,
            },
          }),
        },
        { headStartMs: 3000, maxRetries: 1, backoffMs: 1500 },
      );

      if (networkError) {
        console.error('[ArticleEdit] Network error (na retry):', networkError);
        throw new Error(
          'Kon het verzoek niet verzenden. Controleer je internetverbinding en probeer het opnieuw.'
        );
      }

      console.log('[ArticleEdit] Request sent! Polling database for updated article_content...');

      const POLLING_MAX_ATTEMPTS = 120;
      const POLLING_INTERVAL = 2000;

      const verified = await waitForSessionUpdate(
        activeSession?.id || '',
        'article_content',
        POLLING_MAX_ATTEMPTS,
        POLLING_INTERVAL,
        originalContent
      );

      if (!verified) {
        await logError({
          errorType: 'article_adjust_timeout',
          errorMessage: 'Article regeneration timed out - content not updated in database',
          sessionId: activeSession?.id,
        });
        throw new Error('Artikel werd niet aangepast binnen verwachte tijd. Dit kan betekenen dat de AI server overbelast is. Probeer het later opnieuw.');
      }

      console.log('[ArticleEdit] Article content updated in database. Fetching new content...');

      const { data: updatedSession, error: fetchError } = await supabase
        .from('sessions')
        .select('article_content, article_last_edited')
        .eq('id', activeSession?.id)
        .maybeSingle();

      if (fetchError || !updatedSession?.article_content) {
        throw new Error('Kon aangepast artikel niet ophalen uit database');
      }

      const htmlContent = ensureHtmlContent(updatedSession.article_content);
      latestHtmlRef.current = htmlContent;
      editor.commands.setContent(htmlContent, { emitUpdate: false });

      if (updatedSession.article_last_edited) {
        setLastSaved(new Date(updatedSession.article_last_edited));
      }

      console.log('[ArticleEdit] Article successfully regenerated and displayed!');

      setShowAdjustForm(false);
      setAdjustRequest('');
    } catch (error) {
      console.error('Error regenerating article:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      await logError({
        errorType: 'article_adjust_error',
        errorMessage: `Error adjusting article: ${errorMessage}`,
        stackTrace: error instanceof Error ? error.stack : undefined,
        sessionId: activeSession?.id,
      });
      alert('Er ging iets mis bij het aanpassen van je artikel. Probeer het opnieuw.');
    } finally {
      setRegenerating(false);
    }
  };

  const addLink = () => {
    const url = prompt('Voer de URL in:');
    if (url && editor) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  useEffect(() => {
    return () => {
      flushPendingSave();
    };
  }, []);

  // Tabblad sluiten met niet-opgeslagen wijzigingen: browser vraagt om bevestiging.
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const steps = getWizardSteps(activeSession, 3);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
          <p className="text-gray-600">Laden...</p>
        </div>
      </div>
    );
  }

  if (sessionValidationError) {
    return (
      <div className="min-h-screen bg-[#F2F2EF] flex items-center justify-center">
        <div className="max-w-2xl mx-auto mt-8 px-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-red-800 mb-3">Sessie Validatie Fout</h2>
            <p className="text-red-700 mb-4">{sessionValidationError}</p>
            <button
              onClick={() => navigate(WIZARD_EXIT_ROUTE)}
              className="btn-primary"
            >
              Naar Mijn Blogs
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!loading && !activeSession) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader title="Artikel bewerken" onClose={() => navigate(WIZARD_EXIT_ROUTE)} hideSaveStatus />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">Geen sessie gevonden. Open de blog opnieuw vanuit je concepten.</p>
            <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary mt-4">Naar Mijn Blogs</button>
          </div>
        </main>
      </div>
    );
  }

  if (!activeSession || !activeSession.article_content) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <LoadingSpinner />
      </div>
    );
  }

  if (regenerating) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <LoadingSpinner text="We passen je artikel aan..." />
      </div>
    );
  }

  if (!editor) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <StepIndicator steps={steps} onStepClick={handleStepClick} />
        <LoadingSpinner text="Editor laden..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Artikel bewerken"
        onBack={goBack}
        onClose={handleClose}
        hasUnsavedChanges={dirty}
      />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <StepIndicator
          steps={steps}
          onStepClick={(step) => {
            void saveThen(() => handleStepClick(step));
          }}
        />

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#231F20] mb-2">Bewerk je artikel</h1>
          <p className="text-gray-600 mb-6">
            Pas je artikel aan met de editor hieronder. Je kunt tekst formatteren, links toevoegen en meer. Wijzigingen worden automatisch opgeslagen.
          </p>

          {/* Action Button */}
          <button
            onClick={handleContinue}
            disabled={navigating || saving}
            className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {navigating || saving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Opslaan...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Verder
              </>
            )}
          </button>

          {/* Unsaved Changes Indicator */}
          {dirty && (
            <div className="flex items-center gap-2 text-amber-600 text-sm mt-3">
              <div className="w-2 h-2 bg-amber-600 rounded-full" />
              <span>Je hebt niet-opgeslagen wijzigingen</span>
            </div>
          )}

          {/* Last Saved Indicator */}
          {lastSaved && (
            <p className="text-gray-500 text-sm mt-3">
              Laatst opgeslagen: {lastSaved.toLocaleTimeString('nl-NL')}
            </p>
          )}
        </div>

        <div className="space-y-6">
        <div>
        <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
          <div className="bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-1">
            <button
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('bold') ? 'bg-gray-300' : ''
              }`}
              title="Bold"
            >
              <Bold className="w-5 h-5" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('italic') ? 'bg-gray-300' : ''
              }`}
              title="Italic"
            >
              <Italic className="w-5 h-5" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('underline') ? 'bg-gray-300' : ''
              }`}
              title="Underline"
            >
              <UnderlineIcon className="w-5 h-5" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef08a' }).run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('highlight') ? 'bg-gray-300' : ''
              }`}
              title="Highlight"
            >
              <Highlighter className="w-5 h-5" />
            </button>
            <div className="w-px bg-gray-300 mx-1" />
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''
              }`}
              title="Heading 2"
            >
              <Heading2 className="w-5 h-5" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('heading', { level: 3 }) ? 'bg-gray-300' : ''
              }`}
              title="Heading 3"
            >
              <Heading3 className="w-5 h-5" />
            </button>
            <div className="w-px bg-gray-300 mx-1" />
            <button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('bulletList') ? 'bg-gray-300' : ''
              }`}
              title="Bullet List"
            >
              <List className="w-5 h-5" />
            </button>
            <button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('orderedList') ? 'bg-gray-300' : ''
              }`}
              title="Numbered List"
            >
              <ListOrdered className="w-5 h-5" />
            </button>
            <div className="w-px bg-gray-300 mx-1" />
            <button
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('blockquote') ? 'bg-gray-300' : ''
              }`}
              title="Quote"
            >
              <Quote className="w-5 h-5" />
            </button>
            <button
              onClick={addLink}
              className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                editor.isActive('link') ? 'bg-gray-300' : ''
              }`}
              title="Link toevoegen"
            >
              <Link2 className="w-5 h-5" />
            </button>
            <button
              onClick={images.openImagePicker}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Afbeelding toevoegen"
            >
              <ImagePlus className="w-5 h-5" />
            </button>
            <button
              onClick={images.openGroupPicker}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Beeldgroep toevoegen (meerdere foto's naast elkaar)"
            >
              <LayoutGrid className="w-5 h-5" />
            </button>
            <div className="w-px bg-gray-300 mx-1" />
            <button
              onClick={() => editor.chain().focus().undo().run()}
              disabled={!editor.can().undo()}
              className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Ongedaan maken"
            >
              <Undo className="w-5 h-5" />
            </button>
            <button
              onClick={() => editor.chain().focus().redo().run()}
              disabled={!editor.can().redo()}
              className="p-2 rounded hover:bg-gray-200 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              title="Opnieuw"
            >
              <Redo className="w-5 h-5" />
            </button>
          </div>
          <div className="blog-editor">
            <EditorContent editor={editor} />
          </div>
        </div>
        {imageError && <p className="text-red-600 text-sm mt-2">{imageError}</p>}
        </div>

        <div className="space-y-3 mt-6">
          {!showAdjustForm ? (
            <button
              onClick={() => setShowAdjustForm(true)}
              className="w-full text-gray-600 hover:text-[#231F20] py-3 flex items-center justify-center gap-2 font-medium transition-colors"
            >
              <Sparkles className="w-4 h-4" />
              Laat AI het aanpassen
            </button>
          ) : (
            <div className="card bg-blue-50 border-blue-200 space-y-3">
              <p className="font-semibold text-[#231F20]">Wat moet er anders?</p>
              <textarea
                className="textarea-field min-h-[100px]"
                placeholder="Bijvoorbeeld: maak het minder technisch, voeg een voorbeeld toe over X, verander de tone naar..."
                value={adjustRequest}
                onChange={(e) => setAdjustRequest(e.target.value)}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={handleAdjust}
                  disabled={!adjustRequest.trim()}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Pas aan
                </button>
                <button
                  onClick={() => {
                    setShowAdjustForm(false);
                    setAdjustRequest('');
                  }}
                  className="btn-secondary"
                >
                  Annuleren
                </button>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
      <EditorImageTools {...images.toolsProps} />
    </div>
  );
}
